import { Component } from '@theme/component';
import { fetchConfig, preloadImage, onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, CartUpdateEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

/**
 * @param {HTMLElement | null | undefined} checkerRoot
 * @returns {number | null}
 */
function parseInstallVariantIdFromDeliveryConfig(checkerRoot) {
  const el = checkerRoot?.querySelector?.('[data-delivery-checker-config]');
  if (!el?.textContent) return null;
  try {
    const cfg = JSON.parse(el.textContent);
    const id = cfg?.installProduct?.variantId;
    if (id == null) return null;
    const vid = typeof id === 'number' ? id : parseInt(String(id), 10);
    return Number.isFinite(vid) && vid > 0 ? vid : null;
  } catch (_) {
    return null;
  }
}

/**
 * Pincode check succeeded for this delivery-checker block (install row may still have `hidden` in DOM edge cases).
 * @param {HTMLElement} checker
 * @returns {boolean}
 */
function isDeliveryCheckerPincodeSuccess(checker) {
  const el = checker.querySelector('[data-msg="success"]');
  return el instanceof HTMLElement && !el.hidden;
}

/** @type {string} keep in sync with product-delivery-checker.js */
const QUBO_DELIVERY_PINCODE_LS = 'qubo_delivery_pincode';

/** Links main product + installation line items (same helper idea as install intercept in delivery-checker). */
function randomSevenDigitPairId() {
  return String(Math.floor(1000000 + Math.random() * 9000000));
}

/**
 * @param {FormData} formData
 * @returns {Record<string, string>}
 */
function extractLineItemProperties(formData) {
  /** @type {Record<string, string>} */
  const properties = {};

  for (const [key, rawValue] of formData.entries()) {
    if (typeof key !== 'string') continue;
    if (!key.startsWith('properties[') || !key.endsWith(']')) continue;

    const propertyName = key.slice(11, -1).trim();
    if (!propertyName) continue;

    const value = String(rawValue ?? '').trim();
    if (!value) continue;

    properties[propertyName] = value;
  }

  return properties;
}

/**
 * @param {string | undefined} productId
 * @returns {HTMLElement | null}
 */
function getDeliveryCheckerForProduct(productId) {
  if (!productId) return null;
  const checker = document.querySelector(
    `[data-product-delivery-checker][data-product-id="${productId}"]`
  );
  return checker instanceof HTMLElement ? checker : null;
}

/**
 * @param {HTMLElement} checker
 * @returns {boolean}
 */
function isPincodeRequiredForChecker(checker) {
  const configEl = checker.querySelector('[data-delivery-checker-config]');
  if (configEl?.textContent) {
    try {
      const cfg = JSON.parse(configEl.textContent);
      if (cfg.installationAvailable === false) return false;
      if (cfg.sheetUrl) return true;
    } catch {
      /* fall through to data attribute */
    }
  }
  return checker.getAttribute('data-installation-available') === 'true';
}

/**
 * PDP pincode: block ATC until Check succeeds for the pincode in the field (matches LS).
 * @param {HTMLElement | string | undefined} productFormOrProductId - `product-form-component` or product id
 * @returns {boolean} true when add-to-cart must be blocked
 */
function pincodeGateBlocksSubmit(productFormOrProductId) {
  const productId =
    typeof productFormOrProductId === 'string'
      ? productFormOrProductId
      : productFormOrProductId?.dataset?.productId;
  if (!productId) return false;

  const checker = getDeliveryCheckerForProduct(productId);
  if (!checker || !isPincodeRequiredForChecker(checker)) return false;

  const input = checker.querySelector('[data-pincode-input]');
  const raw = (input instanceof HTMLInputElement ? input.value : '').replace(/\D/g, '');
  const ls = localStorage.getItem(QUBO_DELIVERY_PINCODE_LS);

  const w = /** @type {Window & { __quboPincodeGate?: Record<string, { checked?: boolean; blocked?: boolean; installServiceable?: boolean | null }> }} */ (
    window
  );
  const gateRaw = w.__quboPincodeGate?.[productId];
  const gate =
    gateRaw !== null && gateRaw !== undefined && typeof gateRaw === 'object'
      ? gateRaw
      : { checked: false, blocked: false };

  if (gate.blocked) return true;
  if (!gate.checked || raw.length !== 6 || raw !== ls) return true;
  if (!isDeliveryCheckerPincodeSuccess(checker)) return true;
  return false;
}

/**
 * @param {string | undefined} productId
 * @returns {boolean}
 */
function pincodeGateBlocksProductId(productId) {
  return pincodeGateBlocksSubmit(productId);
}

/**
 * @param {string | undefined} productId
 */
function triggerQuboPincodePrompt(productId) {
  if (!productId) return;
  const w = /** @type {Window & { __quboPincodePromptByProduct?: Record<string, () => void> }} */ (window);
  const fn = w.__quboPincodePromptByProduct?.[productId];
  if (typeof fn === 'function') fn();
}

if (typeof window !== 'undefined') {
  const w = /** @type {Window & {
   *   __quboPincodeGateBlocksSubmit?: typeof pincodeGateBlocksSubmit;
   *   __quboPincodeGateBlocksProductId?: typeof pincodeGateBlocksProductId;
   *   __quboTriggerPincodePrompt?: typeof triggerQuboPincodePrompt;
   * }} */ (window);
  w.__quboPincodeGateBlocksSubmit = pincodeGateBlocksSubmit;
  w.__quboPincodeGateBlocksProductId = pincodeGateBlocksProductId;
  w.__quboTriggerPincodePrompt = triggerQuboPincodePrompt;

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (form.getAttribute('data-type') !== 'add-to-cart-form') return;

      const productForm = form.closest('product-form-component');
      if (!(productForm instanceof HTMLElement)) return;
      if (!pincodeGateBlocksSubmit(productForm)) return;

      event.preventDefault();
      event.stopPropagation();
      triggerQuboPincodePrompt(productForm.dataset.productId);
    },
    true
  );
}

/**
 * Installation variant when: same product (if data-product-id set), pincode OK, checkbox checked.
 * Does not rely on `[data-installation-product]` hidden state (avoids false negatives).
 * @param {HTMLElement} productForm - `product-form-component` element
 * @returns {number | null}
 */
function getCheckedInstallationVariantId(productForm) {
  const productId = productForm.dataset?.productId;
  const w = /** @type {Window & { __quboPincodeGate?: Record<string, { checked?: boolean; blocked?: boolean; installServiceable?: boolean | null }> }} */ (
    window
  );
  const checkers = document.querySelectorAll('[data-product-delivery-checker]');

  for (let i = 0; i < checkers.length; i++) {
    const checker = checkers[i];
    if (!(checker instanceof HTMLElement)) continue;

    if (
      productId &&
      checker.dataset.productId &&
      String(checker.dataset.productId) !== String(productId)
    ) {
      continue;
    }

    if (!isDeliveryCheckerPincodeSuccess(checker)) continue;

    const checkerProductId = checker.dataset.productId ? String(checker.dataset.productId) : (productId ? String(productId) : '');
    if (checkerProductId) {
      const gate = w.__quboPincodeGate?.[checkerProductId];
      if (gate && typeof gate === 'object' && gate.installServiceable === false) continue;
    }

    const cb = checker.querySelector('[data-install-checkbox]');
    if (!cb || !(cb instanceof HTMLInputElement) || cb.type !== 'checkbox' || !cb.checked) continue;

    let vid = parseInt(String(cb.value || '').trim(), 10);
    if (!Number.isFinite(vid) || vid <= 0) {
      vid = parseInstallVariantIdFromDeliveryConfig(checker) ?? 0;
    }
    if (!Number.isFinite(vid) || vid <= 0) continue;
    return vid;
  }

  return null;
}

/**
 * @param {HTMLElement} productForm
 * @returns {number | null}
 */
function getSelectedSubscriptionVariantId(productForm) {
  const productId = productForm.dataset?.productId;
  const scopeRoot = productForm.closest('.shopify-section, dialog, product-card');
  const scope = scopeRoot instanceof HTMLElement ? scopeRoot : document;
  const picker =
    (productId
      ? scope.querySelector(`variant-picker[data-product-id="${productId}"]`)
      : scope.querySelector('variant-picker')) ??
    (productId ? document.querySelector(`variant-picker[data-product-id="${productId}"]`) : document.querySelector('variant-picker'));

  if (!(picker instanceof HTMLElement)) return null;

  const checkedInput = picker.querySelector('[data-subscription-option]:checked');
  if (!(checkedInput instanceof HTMLInputElement)) return null;

  const variantId = parseInt(String(checkedInput.value || '').trim(), 10);
  return Number.isFinite(variantId) && variantId > 0 ? variantId : null;
}

/**
 * AI Guard FBT card selected in `product-related-color-products` (metafield plans).
 * @param {HTMLElement} productForm
 * @returns {number | null}
 */
function getSelectedFbtBundleVariantId(productForm) {
  const productId = productForm.dataset?.productId;
  if (!productId) return null;

  const scope = document.querySelector(
    `[data-has-ai-guard-bundle="true"][data-pdp-product-id="${productId}"]`
  );
  if (!(scope instanceof HTMLElement)) return null;

  const card =
    scope.querySelector('[data-related-product-card].is-selected') ||
    scope.querySelector('[data-related-product-card]');
  if (!(card instanceof HTMLElement)) return null;

  const btn = card.querySelector('[data-related-product-add-button][data-variant-id]');
  if (!(btn instanceof HTMLElement)) return null;

  const variantId = parseInt(String(btn.getAttribute('data-variant-id') || '').trim(), 10);
  return Number.isFinite(variantId) && variantId > 0 ? variantId : null;
}

/**
 * @param {HTMLElement} productForm
 * @returns {boolean}
 */
function isBasicAiGuardPlanSelected(productForm) {
  const productId = productForm.dataset?.productId;
  const scopeRoot = productForm.closest('.shopify-section, dialog, product-card');
  const scope = scopeRoot instanceof HTMLElement ? scopeRoot : document;
  const picker =
    (productId
      ? scope.querySelector(`variant-picker[data-product-id="${productId}"]`)
      : scope.querySelector('variant-picker')) ??
    (productId ? document.querySelector(`variant-picker[data-product-id="${productId}"]`) : document.querySelector('variant-picker'));

  if (!(picker instanceof HTMLElement)) return false;

  const checkedInput = picker.querySelector('[data-subscription-option]:checked');
  if (!(checkedInput instanceof HTMLInputElement)) return false;

  return String(checkedInput.value || '').trim() === '';
}

/**
 * Subscription / AI Guard plan line for cart: explicit FBT card click, else variant picker / selected FBT card.
 * @param {HTMLElement} productForm
 * @returns {number | null}
 */
function resolveBundleVariantIdForCart(productForm) {
  const pending = productForm.dataset.pendingBundleVariantId;
  if (pending) {
    delete productForm.dataset.pendingBundleVariantId;
    const id = parseInt(String(pending).trim(), 10);
    if (Number.isFinite(id) && id > 0) return id;
  }

  const fromPicker = getSelectedSubscriptionVariantId(productForm);
  if (fromPicker != null) return fromPicker;

  if (isBasicAiGuardPlanSelected(productForm)) return null;

  return getSelectedFbtBundleVariantId(productForm);
}

/**
 * Metadata for GTM / `qw_add_to_bag` when the subscription add-on line is added.
 * Reads metadata embedded on the selected subscription option in the variant picker.
 *
 * @param {HTMLElement} productForm - `product-form-component` element
 * @returns {Record<string, string | number | boolean | null> | null}
 */
function getSelectedSubscriptionTrackingMeta(productForm) {
  const productId = productForm.dataset?.productId;
  const scopeRoot = productForm.closest('.shopify-section, dialog, product-card');
  const scope = scopeRoot instanceof HTMLElement ? scopeRoot : document;
  const picker =
    (productId
      ? scope.querySelector(`variant-picker[data-product-id="${productId}"]`)
      : scope.querySelector('variant-picker')) ??
    (productId ? document.querySelector(`variant-picker[data-product-id="${productId}"]`) : document.querySelector('variant-picker'));

  if (!(picker instanceof HTMLElement)) return null;

  const checkedInput = picker.querySelector('[data-subscription-option]:checked');
  if (!(checkedInput instanceof HTMLInputElement)) return null;

  const variantId = String(checkedInput.dataset.subscriptionVariantId || checkedInput.value || '').trim();
  if (!variantId) return null;

  const stockValue = checkedInput.dataset.subscriptionProductInStock;
  let productInStock = null;
  if (stockValue === 'true') productInStock = true;
  if (stockValue === 'false') productInStock = false;

  return {
    productId: checkedInput.dataset.subscriptionProductId || null,
    productTitle: checkedInput.dataset.subscriptionProductTitle || null,
    productType: checkedInput.dataset.subscriptionProductType || null,
    variantId,
    productPrice: checkedInput.dataset.subscriptionProductPrice || null,
    productImage: checkedInput.dataset.subscriptionProductImage || null,
    productInStock,
  };
}

/**
 * Sends a dedicated GTM add-to-bag event for linked add-ons like subscriptions.
 *
 * @param {Record<string, string | number | boolean | null> | null} meta
 * @param {number} quantity
 */
function trackLinkedAddonAddToBag(meta, quantity) {
  if (!meta) return;

  const detail = {
    product_title: meta.productTitle ?? null,
    product_type: meta.productType ?? null,
    product_id: meta.productId ?? null,
    product_price: meta.productPrice ?? null,
    product_quantity: quantity,
    product_in_stock: meta.productInStock,
    product_variant: meta.variantId ?? null,
    product_image: meta.productImage ?? null,
  };

  const w = /** @type {Window & { trackQwAddToBag?: (data: typeof detail) => void }} */ (window);
  if (typeof w.trackQwAddToBag === 'function') {
    w.trackQwAddToBag(detail);
    return;
  }

  document.dispatchEvent(new CustomEvent('qw:add-to-cart', { detail, bubbles: true }));
}

/**
 * Metadata for GTM / `qw_add_to_bag` when the installation add-on line is added.
 * Reads `[data-delivery-checker-config].installProduct` for the checker that matched install ATC.
 *
 * @param {HTMLElement} productForm - `product-form-component` element
 * @returns {Record<string, string | number | boolean | null> | null}
 */
function getInstallationProductTrackingMeta(productForm) {
  const productIdAttr = productForm.dataset?.productId;
  const w = /** @type {Window & { __quboPincodeGate?: Record<string, { checked?: boolean; blocked?: boolean; installServiceable?: boolean | null }> }} */ (
    window
  );
  const checkers = document.querySelectorAll('[data-product-delivery-checker]');

  for (let i = 0; i < checkers.length; i++) {
    const checker = checkers[i];
    if (!(checker instanceof HTMLElement)) continue;

    if (
      productIdAttr &&
      checker.dataset.productId &&
      String(checker.dataset.productId) !== String(productIdAttr)
    ) {
      continue;
    }

    if (!isDeliveryCheckerPincodeSuccess(checker)) continue;

    const checkerProductId = checker.dataset.productId ? String(checker.dataset.productId) : (productIdAttr ? String(productIdAttr) : '');
    if (checkerProductId) {
      const gate = w.__quboPincodeGate?.[checkerProductId];
      if (gate && typeof gate === 'object' && gate.installServiceable === false) continue;
    }

    const cb = checker.querySelector('[data-install-checkbox]');
    if (!cb || !(cb instanceof HTMLInputElement) || cb.type !== 'checkbox' || !cb.checked) continue;

    const configEl = checker.querySelector('[data-delivery-checker-config]');
    if (!configEl?.textContent) continue;

    try {
      const cfg = JSON.parse(configEl.textContent);
      const ip = cfg?.installProduct;
      if (!ip || ip.variantId == null) return null;

      return {
        productId: ip.productId != null ? String(ip.productId) : null,
        productTitle: ip.title || null,
        productType: ip.type || null,
        variantId: String(ip.variantId),
        productPrice: ip.price != null ? String(ip.price) : null,
        productImage: ip.image || null,
        productInStock: true,
      };
    } catch {
      return null;
    }
  }

  return null;
}

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    }
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    const productForm = /** @type {ProductFormComponent | null} */ (this.closest('product-form-component'));
    if (productForm && pincodeGateBlocksSubmit(productForm)) {
      triggerQuboPincodePrompt(productForm.dataset.productId);
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      return;
    }

    // Check if adding would exceed max before animating
    const quantitySelector = productForm?.getActiveQuantitySelector?.();
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }
    if (this.refs.addToCartButton.dataset.puppet !== 'true') {
      const animationEnabled = this.dataset.addToCartAnimation === 'true';
      if (animationEnabled && !event.target.closest('.quick-add-modal')) {
        this.#animateFlyToCart();
      }
      this.animateAddToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    let flyToCartClass = addToCartButton.classList.contains('quick-add__button')
      ? 'fly-to-cart--quick'
      : 'fly-to-cart--main';

    flyToCartElement.classList.add(flyToCartClass);
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.style.setProperty('--start-opacity', '0');
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart = async function () {
    const { addToCartButton } = this.refs;

    // Initialize the array if it doesn't exist
    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }

    // Clear all existing timeouts
    this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    this.#resetTimeouts = [];

    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }

    await onAnimationEnd(addToCartButton);

    // Create new timeout and store it in the array
    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');

      // Remove this timeout from the array
      const index = this.#resetTimeouts.indexOf(timeoutId);
      if (index > -1) {
        this.#resetTimeouts.splice(index, 1);
      }
    }, 800);

    this.#resetTimeouts.push(timeoutId);
  };
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing component.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label element.
 * @property {HTMLElement | undefined} pricePerItem - The price per item component.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * How many cameras tier picker — only when product metafield enables it.
   * AI Guard / subscription add-ons use separate logic and are unaffected.
   * @returns {boolean}
   */
  #hasHowManyCamerasEnabled() {
    return this.dataset.howManyCamerasEnabled === 'true';
  }

  /**
   * Quantity selector for cart sync — scoped so standard PDP ATC is unchanged.
   * @returns {HTMLElement | null}
   */
  /**
   * How many cameras block may render outside buy-buttons; quantity still links via form attribute.
   * @returns {HTMLElement | null}
   */
  #findExternalHowManyCamerasSelector() {
    const productId = this.dataset.productId;
    const section = this.closest('.shopify-section');
    if (!section) return null;

    const blocks = section.querySelectorAll('[data-how-many-cameras-block]');
    for (const block of blocks) {
      if (!(block instanceof HTMLElement)) continue;
      if (productId && block.dataset.productId && block.dataset.productId !== productId) continue;
      const selector = block.querySelector('qubo-camera-quantity-selector-component');
      if (selector instanceof HTMLElement) return selector;
    }

    return null;
  }

  #findQuantitySelectorElement() {
    if (this.#hasHowManyCamerasEnabled()) {
      const external = this.#findExternalHowManyCamerasSelector();
      if (external) return external;
      return this.querySelector('qubo-camera-quantity-selector-component');
    }
    return this.querySelector('quantity-selector-component');
  }

  /**
   * @returns {any | undefined}
   */
  #getActiveQuantitySelector() {
    if (this.#hasHowManyCamerasEnabled()) {
      const external = this.#findExternalHowManyCamerasSelector();
      if (external) return external;
    }
    return this.refs.quantitySelector ?? this.#findQuantitySelectorElement();
  }

  /**
   * Quantity selector used for ATC validation (inline or external how-many-cameras block).
   * @returns {any | undefined}
   */
  getActiveQuantitySelector() {
    return this.#getActiveQuantitySelector();
  }

  /**
   * Selector for quantity input in variant-update section HTML.
   * @returns {string}
   */
  #quantityInputSelectorForVariantUpdate() {
    return this.#hasHowManyCamerasEnabled()
      ? '[data-how-many-cameras-block] qubo-camera-quantity-selector-component input[ref="quantityInput"], qubo-camera-quantity-selector-component input[ref="quantityInput"]'
      : 'quantity-selector-component input[ref="quantityInput"]';
  }

  /**
   * Updates quantity selector with cart data for current variant
   * @param {Cart} cart - The cart object with items array
   * @returns {number} The cart quantity for the current variant
   */
  #updateCartQuantityFromData(cart) {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value || !cart?.items) return 0;

    const cartItem = cart.items.find((item) => item.variant_id.toString() === variantIdInput.value.toString());
    const cartQty = cartItem ? cartItem.quantity : 0;

    // Use public API to update quantity selector (standard or how-many-cameras only)
    const quantitySelector = /** @type {any | undefined} */ (this.#findQuantitySelectorElement());
    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    // Update quantity label if it exists
    this.#updateQuantityLabel(cartQty);

    return cartQty;
  }

  /**
   * Fetches cart and updates quantity selector for current variant
   * @returns {Promise<number>} The cart quantity for the current variant
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value) return 0;

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      return this.#updateCartQuantityFromData(cart);
    } catch (error) {
      console.error('Failed to fetch cart quantity:', error);
      return 0;
    }
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    // Skip if this event came from this component
    if (event.detail?.sourceId === this.id || event.detail?.data?.source === 'product-form-component') return;

    const cart = /** @type {Cart} */ (event.detail?.resource);
    if (cart?.items) {
      this.#updateCartQuantityFromData(cart);
    } else {
      await this.#fetchAndUpdateCartQuantity();
    }
  };

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  handleSubmit(event) {
    const { addToCartTextError } = this.refs;
    // Stop default behaviour from the browser
    event.preventDefault();

    if (pincodeGateBlocksSubmit(this)) {
      triggerQuboPincodePrompt(this.dataset.productId);
      return;
    }

    if (this.#timeout) clearTimeout(this.#timeout);

    // Query for ALL add-to-cart components
    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );

    // Check if ANY add to cart button is disabled and do an early return if it is
    const anyButtonDisabled = Array.from(allAddToCartContainers).some(
      (container) => container.refs.addToCartButton?.disabled
    );
    if (anyButtonDisabled) return;

    // Send the add to cart information to the cart
    const form = this.querySelector('form');

    if (!form) throw new Error('Product form element missing');

    const activeQuantitySelector = this.#getActiveQuantitySelector();
    if (activeQuantitySelector?.canAddToCart) {
      const validation = activeQuantitySelector.canAddToCart();

      if (!validation.canAdd) {
        // Disable ALL add-to-cart buttons
        for (const container of allAddToCartContainers) {
          container.disable();
        }

        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity?.toString() || '');
        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            const newTextNode = document.createTextNode(errorMessage);
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(errorMessage);

          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          // Re-enable ALL add-to-cart buttons
          for (const container of allAddToCartContainers) {
            container.enable();
          }
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    const formData = new FormData(form);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    /** @type {string[]} */
    const cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    });
    const sectionIdsJoined = cartItemComponentsSectionIds.join(',');

    const mainVariantId = formData.get('id');
    const quantity = Number(formData.get('quantity')) || Number(this.dataset.quantityDefault) || 1;
    const installVariantId = getCheckedInstallationVariantId(this);
    const subscriptionVariantId = resolveBundleVariantIdForCart(this);
    const shouldAddInstall =
      installVariantId != null &&
      mainVariantId != null &&
      String(installVariantId) !== String(mainVariantId);
    const shouldAddSubscription =
      subscriptionVariantId != null &&
      mainVariantId != null &&
      String(subscriptionVariantId) !== String(mainVariantId) &&
      String(subscriptionVariantId) !== String(installVariantId ?? '');

    let pairIdForInstallAddon = '';
    let pairIdForSubscriptionAddon = '';
    /** @type {Record<string, string>} */
    const mainItemProperties = extractLineItemProperties(formData);

    if (shouldAddInstall) {
      pairIdForInstallAddon = randomSevenDigitPairId();
      mainItemProperties._pairId = pairIdForInstallAddon;
    }
    if (shouldAddSubscription) {
      pairIdForSubscriptionAddon = randomSevenDigitPairId();
      mainItemProperties._sub_pairid = pairIdForSubscriptionAddon;
    }

    /** @type {Record<string, any>} */
    const mainCartItem = {
      id: Number(mainVariantId),
      quantity,
    };

    if (Object.keys(mainItemProperties).length) {
      mainCartItem.properties = mainItemProperties;
    }

    const sellingPlan = formData.get('selling_plan');
    if (sellingPlan != null && String(sellingPlan).trim() !== '') {
      mainCartItem.selling_plan = String(sellingPlan).trim();
    }

    /** @type {Array<Record<string, any>>} */
    const items = [mainCartItem];

    if (shouldAddInstall) {
      items.push({
        id: Number(installVariantId),
        quantity,
        properties: { _pairId: pairIdForInstallAddon },
      });
    }

    if (shouldAddSubscription) {
      // AI Guard subscription add-on is always one unit; main line uses form quantity (cameras / +/-).
      items.push({
        id: Number(subscriptionVariantId),
        quantity: 1,
        properties: { _sub_pairid: pairIdForSubscriptionAddon },
      });
    }

    /** @type {{ items: Array<Record<string, any>>, sections?: string }} */
    const cartAddPayload = { items };
    if (sectionIdsJoined) {
      cartAddPayload.sections = sectionIdsJoined;
    }

    if (pincodeGateBlocksSubmit(this)) {
      triggerQuboPincodePrompt(this.dataset.productId);
      return;
    }

    const fetchCfg = fetchConfig('json', { body: JSON.stringify(cartAddPayload) });
    const addPromise = fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
    }).then((response) => response.json());

    addPromise
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
          );

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: quantity,
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = mainVariantId;

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          if (shouldAddInstall || shouldAddSubscription) {
            try {
              const cartRes = await fetch('/cart.js');
              const cartJson = await cartRes.json();
              if (shouldAddInstall) {
                const installTrack = getInstallationProductTrackingMeta(this);
                const installCartUpdateDetail = /** @type {any} */ ({
                  source: 'product-form-install-addon',
                  productId: installTrack?.productId != null ? String(installTrack.productId) : undefined,
                  productTitle: installTrack?.productTitle ?? undefined,
                  productType: installTrack?.productType ?? undefined,
                  variantId: installTrack?.variantId != null ? String(installTrack.variantId) : undefined,
                  productPrice: installTrack?.productPrice ?? undefined,
                  productImage: installTrack?.productImage ?? undefined,
                  productInStock: installTrack?.productInStock,
                  addQuantity: quantity,
                });
                document.dispatchEvent(new CartUpdateEvent(cartJson, this.id, installCartUpdateDetail));
              }

              if (shouldAddSubscription) {
                const subscriptionTrack = getSelectedSubscriptionTrackingMeta(this);
                trackLinkedAddonAddToBag(subscriptionTrack, 1);
              }
            } catch (_) {
              /* ignore */
            }
          }

          // Add aria-live region to inform screen readers that the item was added
          // Get the added text from any add-to-cart button
          const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
          if (anyAddToCartButton) {
            const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, SUCCESS_MESSAGE_DISPLAY_DURATION);
          }

          // Fetch the updated cart to get the actual total quantity for this variant
          await this.#fetchAndUpdateCartQuantity();

          const cartAddDetail = /** @type {any} */ ({
            source: 'product-form-component',
            itemCount: quantity,
            productId: this.dataset.productId,
            variantId: String(id),
            productTitle: this.dataset.productTitle || null,
            productType: this.dataset.productType || null,
            productPrice: this.dataset.productPrice || null,
            productImage: this.dataset.productImage || null,
            productInStock: this.dataset.productInStock,
            sections: response.sections,
          });
          this.dispatchEvent(new CartAddEvent({}, id.toString(), cartAddDetail));
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        cartPerformance.measureFromEvent('add:user-action', event);
      });
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */ (newElement.cloneNode(true)));
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId } = this.refs;

    // Update the variant ID
    variantId.value = event.detail.resource?.id ?? '';
    const { addToCartButtonContainer: currentAddToCartButtonContainer, acceleratedCheckoutButtonContainer } = this.refs;
    const currentAddToCartButton = currentAddToCartButtonContainer?.refs.addToCartButton;

    // Update state and text for add-to-cart button
    if (!currentAddToCartButtonContainer || (!currentAddToCartButton && !acceleratedCheckoutButtonContainer)) return;

    // Update the button state
    const notifyHide = this.dataset.notifyHide === 'true';
    const notifySubmitButton = !notifyHide && currentAddToCartButton?.hasAttribute('data-notify-submit');
    if (event.detail.resource == null || event.detail.resource.available == false) {
      if (!notifySubmitButton) {
        currentAddToCartButtonContainer.disable();
      } else {
        currentAddToCartButtonContainer.enable();
      }
    } else {
      currentAddToCartButtonContainer.enable();
    }

    const newAddToCartButton = event.detail.data.html.querySelector('product-form-component [ref="addToCartButton"]');
    if (newAddToCartButton && currentAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    const notifyWrap = this.querySelector('[data-pdp-notify-wrap]');
    const notifyRoot = this.querySelector('[data-notify-root]');
    const atcArea = this.querySelector('.buy-buttons-block__atc-area');
    const isVariantAvailable = event.detail.resource != null && event.detail.resource.available !== false;
    const showNotify = !notifyHide && !isVariantAvailable;

    if (notifyWrap) {
      notifyWrap.hidden = !showNotify;
    }

    if (atcArea) {
      atcArea.classList.toggle('pdp-notify-active', showNotify);
    }

    if (notifyRoot && event.detail.resource?.id) {
      const variantId = String(event.detail.resource.id);
      const productPath = (this.dataset.productUrl || '').split('?')[0];
      const variantUrl = productPath
        ? `${window.location.origin}${productPath}?variant=${variantId}`
        : window.location.href;

      notifyRoot.setAttribute('data-variant-id', variantId);
      notifyRoot.setAttribute('data-variant-url', variantUrl);

      const variantInput = notifyRoot.querySelector('input[name="id"]');
      if (variantInput) variantInput.value = variantId;

      const phoneInput = notifyRoot.querySelector('[data-notify-phone]');
      if (phoneInput) {
        phoneInput.setAttribute('data-variantId', variantId);
        phoneInput.setAttribute('data-variantUrl', variantUrl);
      }
    }

    // When OOS, quantity is not rendered in Liquid — morph buttons so it appears/disappears with availability.
    const productFormButtonsEl = this.refs.productFormButtons;
    const newProductFormButtonsForQty = event.detail.data.html.querySelector('.product-form-buttons');
    const currentHasQty = Boolean(
      this.querySelector('quantity-selector-component, qubo-camera-quantity-selector-component, .quantity-selector-wrapper, .qubo-camera-quantity-wrapper')
    );
    const nextHasQty = Boolean(
      newProductFormButtonsForQty &&
        newProductFormButtonsForQty.querySelector(
          'quantity-selector-component, qubo-camera-quantity-selector-component, .quantity-selector-wrapper, .qubo-camera-quantity-wrapper'
        )
    );
    if (productFormButtonsEl && newProductFormButtonsForQty && currentHasQty !== nextHasQty) {
      morph(productFormButtonsEl, newProductFormButtonsForQty);
    }

    if (acceleratedCheckoutButtonContainer) {
      if (event.detail.resource == null || event.detail.resource.available == false) {
        acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      } else {
        acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      }
    }

    // Set the data attribute for the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      if (productVariantMedia) {
        this.refs.addToCartButtonContainer?.setAttribute(
          'data-product-variant-media',
          productVariantMedia + '&width=100'
        );
      }
    }

    // Check if quantity rules, price-per-item, or add-to-cart are appearing/disappearing (causes layout shift)
    const { quantityRules, pricePerItem, productFormButtons, quantityLabel, quantitySelectorWrapper } = this.refs;
    const quantitySelector = this.#getActiveQuantitySelector();

    // Update quantity selector's min/max/step attributes and cart quantity for the new variant
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (
      event.detail.data.html.querySelector(this.#quantityInputSelectorForVariantUpdate())
    );

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      quantitySelector.updateConstraints(newQuantityInput.min, newQuantityInput.max || null, newQuantityInput.step);
    }

    const newQuantityRules = event.detail.data.html.querySelector('.quantity-rules');
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    const newPricePerItem = event.detail.data.html.querySelector('price-per-item');
    const isPricePerItemChanging = !!pricePerItem !== !!newPricePerItem;

    if ((isQuantityRulesChanging || isPricePerItemChanging) && quantitySelector) {
      // Store quantity value before morphing entire container
      const currentQuantityValue = quantitySelector.getValue?.();

      const newProductFormButtons = event.detail.data.html.querySelector('.product-form-buttons');

      if (productFormButtons && newProductFormButtons) {
        morph(productFormButtons, newProductFormButtons);

        // Get the NEW quantity selector after morphing and update its constraints
        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
          event.detail.data.html.querySelector(this.#quantityInputSelectorForVariantUpdate())
        );

        const activeQuantitySelector = this.#getActiveQuantitySelector();
        if (activeQuantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
          // Temporarily set the old value so updateConstraints can snap it properly
          activeQuantitySelector.setValue(currentQuantityValue);
          // updateConstraints will snap to valid increment if needed
          activeQuantitySelector.updateConstraints(
            newQuantityInputElement.min,
            newQuantityInputElement.max || null,
            newQuantityInputElement.step
          );
        }
      }
    } else {
      // Update elements individually when layout isn't changing
      /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
      const morphTargets = [
        ['.quantity-label', quantityLabel, quantitySelector],
        ['.quantity-rules', quantityRules, this.refs.productFormButtons],
        ['price-per-item', pricePerItem, quantitySelectorWrapper],
      ];

      for (const [selector, currentElement, fallback] of morphTargets) {
        this.#morphOrUpdateElement(currentElement, event.detail.data.html.querySelector(selector), fallback);
      }
    }

    // Morph volume pricing if it exists
    const currentVolumePricing = this.refs.volumePricing;
    const newVolumePricing = event.detail.data.html.querySelector('volume-pricing');
    this.#morphOrUpdateElement(currentVolumePricing, newVolumePricing, this.refs.productFormButtons);

    const hasB2BFeatures =
      quantityRules || newQuantityRules || pricePerItem || newPricePerItem || currentVolumePricing || newVolumePricing;

    if (!hasB2BFeatures) return;

    // Fetch and update cart quantity for the new variant
    await this.#fetchAndUpdateCartQuantity();
  };

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}
